import React from 'react'
import { API } from '@Root/API'
import { HandbookTable } from '../HandbookTable'
import { useHandbookApi } from '../useHandbookApi'
import { Callout, ErrorState, LoadingState, Section, Steps } from '../ui'
import styles from '../style.module.scss'

export const StudentCsvImport = () => {
  const { data, loading, error } = useHandbookApi(API.handbook.getStudentImport)

  if (loading) {
    return <LoadingState />
  }
  if (error) {
    return <ErrorState>Unable to load student CSV import rules.</ErrorState>
  }

  return (
    <>
      <div className={styles.pageHead}>
        <p className={styles.kicker}>Students tab</p>
        <p className={styles.lead}>
          CSV import allowlist comes from import.students plus Additional Fields with is_in_csv_import = true. Configure Export does not define import
          columns.
        </p>
      </div>

      <Section title='How import works'>
        <Steps
          items={[
            <>
              Upload a <code>.csv</code> or <code>.txt</code> file (Excel <code>.xlsx</code> is rejected).
            </>,
            'Header row is checked against required column keys (exact strings, case-sensitive).',
            'Each data row is validated against value rules.',
            <>
              Unknown extra columns are ignored. Missing required headers fail with <code>The {'{header}'} field is required.</code>
            </>,
          ]}
        />
      </Section>

      <Section title='Configure Export is not the import list'>
        <Callout warn>
          <p>
            <strong>Configure Export</strong> only chooses which table columns appear in an export. Import does not read that selection.
          </p>
          <p>
            The only shared control is the Additional Field checkbox <strong>Add to CSV import and export</strong>. That flag both shows the field in Configure
            Export and adds it as a required import header using the field name.
          </p>
          <p>Do not re-import an exported CSV. Export writes display titles (Name, Date of birth, SID). Import expects internal keys (name, birthday, sid).</p>
        </Callout>
      </Section>

      <Section title='Export heading vs import header'>
        <HandbookTable
          columns={[
            { key: 'import', label: 'Import header', render: value => <code>{value}</code> },
            { key: 'export', label: 'Typical export heading' },
            { key: 'table_key', label: 'Configure Export / table key', render: value => <code>{value}</code> },
          ]}
          rows={data?.heading_map || []}
        />
      </Section>

      <Section title='Required values' hint='These cells cannot be empty. Other required headers may still have empty cells.'>
        <div className={styles.chipRow}>
          {(data?.required_values || []).map(header => (
            <code key={header}>{header}</code>
          ))}
        </div>
      </Section>

      <Section title='Built-in columns' hint='Header required means the column must exist in the CSV. Optional headers may be omitted.'>
        <HandbookTable
          filter
          columns={[
            { key: 'header', label: 'CSV header', render: value => <code>{value}</code> },
            {
              key: 'header_required',
              label: 'Header',
              render: value => <span className={`${styles.badge} ${value ? styles.badgeRequired : styles.badgeOptional}`}>{value ? 'required' : 'optional'}</span>,
            },
            {
              key: 'value_required',
              label: 'Value',
              render: value => <span className={`${styles.badge} ${value ? styles.badgeRequired : styles.badgeOptional}`}>{value ? 'required' : 'optional'}</span>,
            },
            { key: 'notes', label: 'Notes' },
          ]}
          rows={data?.columns || []}
        />
      </Section>

      <Section
        title='Additional Fields'
        hint='Every person/both Additional Field with Add to CSV import and export becomes a required header. The header must match additional_fields.name exactly.'
      >
        <HandbookTable
          columns={[
            { key: 'name', label: 'CSV header (name)', render: value => <code>{value}</code> },
            { key: 'export_key', label: 'Table / export key', render: value => <code>{value}</code> },
            { key: 'type', label: 'Type' },
            {
              key: 'value_required',
              label: 'Value',
              render: value => <span className={`${styles.badge} ${value ? styles.badgeRequired : styles.badgeOptional}`}>{value ? 'required' : 'optional'}</span>,
            },
            { key: 'notes', label: 'Notes' },
          ]}
          rows={data?.additional_fields || []}
        />
        <Callout>Financial Support bursary (Deductions) is not part of this student contact import.</Callout>
      </Section>
    </>
  )
}
